import { useCallback, useMemo, useState } from "react";
import useDialogHooks from "../../../../../Components/Dialog/dialog-hooks";
import { useGeneralHooks, useToastify } from "../../../../../Hooks/hooks";
import { IImagePresetTypes } from "../../../../../Interfaces/componentTypes";
import { IDineInUnit } from "../../../../../Interfaces/responseTypes";
import { buildDineInQrLink, buildMenuQrLink } from "../../../../../Utilits/utilits";
import { isUnitQrGenerated } from "../Services/Contents/MyServices/DineInUnits/dineInHelpers";
import { copyTextToClipboard, openFacebookShare, openTelegramShare, openWhatsAppShare } from "./qrShare";
import { IQrProps } from "./qr";

export interface IDineInQrSection {
    unit: IDineInUnit;
    typeLabel: string;
    items: {
        number: number;
        imageUrl: string;
        link: string;
        fileName: string;
        badge: string;
        title: string;
    }[];
}

const useMenuQrHooks = (props: IQrProps) => {
    const { qrImage, companyTitle, company_id, dineIn } = props;
    const { baseURL, t } = useGeneralHooks();
    const { open: isDialogOpen, onOpen, onClose: onCloseDialog } = useDialogHooks();
    const { showToastSuccess, showToastError } = useToastify();
    const [width, setWidth] = useState(300);
    const [height, setHeight] = useState(300);
    const [selectedSize, setSelectedSize] = useState<number>(2);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [downloadImageUrl, setDownloadImageUrl] = useState('');
    const [downloadFileName, setDownloadFileName] = useState('qr-code.png');

    const fullImageUrl = `${baseURL}${qrImage}`;
    const menuLink = buildMenuQrLink(companyTitle, company_id);
    const isDineInActive = Boolean(dineIn?.is_active);

    const dineInSections: IDineInQrSection[] = useMemo(() => {
        if (!isDineInActive) {
            return [];
        }

        return (dineIn?.units ?? [])
            .filter((unit) => isUnitQrGenerated(unit) && unit.qr_data?.length)
            .map((unit) => {
                const typeLabel = t(`My_Business.My_Services.Dine_In.Unit_Types.${unit.type}`);

                return {
                    unit,
                    typeLabel,
                    items: (unit.qr_data ?? []).map((qrItem) => {
                        const link = qrItem.link || buildDineInQrLink(
                            companyTitle,
                            company_id,
                            unit.type,
                            qrItem.number,
                        );

                        return {
                            number: qrItem.number,
                            imageUrl: `${baseURL}${qrItem.qr_code}`,
                            link,
                            fileName: `dine-in-${unit.type}-${qrItem.number}.png`,
                            badge: `#${qrItem.number}`,
                            title: typeLabel,
                        };
                    }),
                };
            });
    }, [baseURL, companyTitle, company_id, dineIn?.units, isDineInActive, t]);

    const copyLink = useCallback(async (link: string) => {
        try {
            await copyTextToClipboard(link);
            showToastSuccess(t('My_Business.Qr.Link_Copied'));
        } catch {
            showToastError(t('Toast.Error.Common'));
        }
    }, [showToastError, showToastSuccess, t]);

    const copyAndShareTelegram = useCallback(async (link: string) => {
        try {
            await copyTextToClipboard(link);
            openTelegramShare(link);
        } catch {
            showToastError(t('Toast.Error.Common'));
        }
    }, [showToastError, t]);

    const shareOnWhatsApp = useCallback((link: string) => {
        openWhatsAppShare(link);
    }, []);

    const shareOnFacebook = useCallback((link: string) => {
        openFacebookShare(link);
    }, []);

    const openDownloadDialog = useCallback((imageUrl: string, fileName: string) => {
        setDownloadImageUrl(imageUrl);
        setDownloadFileName(fileName);
        onOpen();
    }, [onOpen]);

    function onSetImageSizes(data: IImagePresetTypes) {
        setSelectedSize(data.id);
        setHeight(data.height);
        setWidth(data.width);
    }

    const handleDownload = () => {
        if (!downloadImageUrl) {
            return;
        }

        setIsLoading(true);
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = downloadImageUrl;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = window.URL.createObjectURL(blob);
                        const anchor = document.createElement('a');
                        anchor.href = url;
                        anchor.download = downloadFileName;
                        document.body.appendChild(anchor);
                        anchor.click();
                        document.body.removeChild(anchor);
                        window.URL.revokeObjectURL(url);
                    }
                    showToastSuccess(t('My_Business.Qr.Downloaded'));
                    setIsLoading(false);
                    onCloseDialog();
                }, 'image/png');
            }
        };

        img.onerror = () => {
            showToastError(t('Toast.Error.Common'));
            setIsLoading(false);
        };
    };

    const PRESET_SIZES: IImagePresetTypes[] = [
        { label: t('Img_Presets.Small'), sizes: '150x150', width: 150, height: 150, id: 1 },
        { label: t('Img_Presets.Normal'), sizes: '300x300', width: 300, height: 300, id: 2 },
        { label: t('Img_Presets.Large'), sizes: '600x600', width: 600, height: 600, id: 3 },
        { label: t('Img_Presets.Full'), sizes: '1080x1080', width: 1080, height: 1080, id: 4 },
    ];

    const cardLabels = useMemo(() => ({
        downloadLabel: t('My_Business.Qr.Dwld_Button_Big'),
        shareLabel: t('Business.Or_Share'),
        linkLabel: t('My_Business.Qr.Link_Label'),
        copyLinkLabel: t('My_Business.Qr.Copy_Link'),
    }), [t]);

    return {
        isLoading,
        isDialogOpen,
        fullImageUrl,
        menuLink,
        selectedSize,
        PRESET_SIZES,
        isDineInActive,
        dineInSections,
        cardLabels,
        downloadImageUrl,
        onCloseDialog,
        handleDownload,
        onSetImageSizes,
        openDownloadDialog,
        copyLink,
        copyAndShareTelegram,
        shareOnWhatsApp,
        shareOnFacebook,
    };
};

export default useMenuQrHooks;
